import React, { useState, useRef, useEffect } from 'react';
import { sendMessage } from '../../Services/chatService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  Send,
  User as UserIcon,
  Loader2,
  RotateCcw,
  Stethoscope,
} from 'lucide-react';
import { historyService } from '../../Services/historyService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ReactMarkdown from 'react-markdown';
import QuotaLimitModal from '../../components/QuotaLimitModal';
import HealthDataConsent from '../../components/HealthDataConsent';
import consentService from '../../Services/consentService.js';
import { useNavigate } from 'react-router-dom';
import { FREE_DAILY_SEARCHES } from '../../config/quotas';

const SUGGESTIONS = [
  { text: 'Interactions médicamenteuses warfarine + AINS ?', icon: '💊', color: 'from-green-400 to-green-500' },
  { text: 'Critères diagnostiques sepsis-3', icon: '🔬', color: 'from-teal-400 to-teal-500' },
  { text: 'Protocole prise en charge IDM ST+', icon: '🫀', color: 'from-emerald-400 to-emerald-500' },
  { text: 'Adaptation posologie IRC stade 4', icon: '🩺', color: 'from-cyan-400 to-cyan-500' },
];

const markdownComponents = {
  h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-4 mb-2" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-4 mb-2" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-base font-bold mt-3 mb-2" {...props} />,
  p: ({ node, ...props }) => (
    <p className="mb-3 leading-relaxed last:mb-0" style={{ whiteSpace: 'pre-line' }} {...props} />
  ),
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-4 space-y-1" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-4 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="mb-1" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-current opacity-80 pl-4 italic my-4" {...props} />
  ),
};

const DashboardMedecin = () => {
  const { user, updateUser, hasPremiumAccess } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Bonjour **Dr. ${user?.last_name}** ! 👋\n\nJe suis **Remadia**, votre assistant clinique. Je fournis des informations médicales détaillées : pharmacocinétique, interactions, protocoles, données probantes.\n\nQuelle est votre question clinique ? 🩺`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedContent, setDisplayedContent] = useState('');
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showHealthConsent, setShowHealthConsent] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const checkConsent = async () => {
      if (user?.uid) {
        const hasConsent = await consentService.hasHealthDataConsent(user.uid);
        if (!hasConsent) setShowHealthConsent(true);
      }
    };
    checkConsent();
  }, [user]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    if (!user?.uid) {
      toast.error('Vous devez être connecté');
      return;
    }

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setLoading(true);

    try {
      const data = await sendMessage(userMessage, messages, null, user.uid);
      const aiResponse = data.content;

      setDisplayedContent(aiResponse);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: aiResponse, timestamp: new Date(), isTyping: false },
      ]);

      // Quota non décompté pendant l'essai (aligné sur chatService.js côté serveur)
      if (!hasPremiumAccess) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const newCount = userDoc.data().daily_search_count || 0;
          updateUser({ daily_search_count: newCount });
          const remaining = Math.max(0, FREE_DAILY_SEARCHES - newCount);
          if (remaining === 0) {
            toast.info(
              `Vous avez atteint votre limite quotidienne de ${FREE_DAILY_SEARCHES} recherches. Revenez demain ou passez à Premium.`,
              { duration: 3000 }
            );
          } else {
            toast.info(
              `${remaining} recherche${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''} aujourd'hui`,
              { duration: 3000 }
            );
          }
        }
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.data()?.settings?.saveHistory !== false) {
          await historyService.saveConversation(user.uid, userMessage, aiResponse);
        }
      } catch (historyError) {
        console.warn('Historique non sauvegardé:', historyError);
      }
    } catch (error) {
      console.error('Erreur chat:', error);
      if (error.message === 'QUOTA_EXCEEDED') {
        setShowQuotaModal(true);
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetConversation = () => {
    setMessages([
      {
        role: 'assistant',
        content: `Nouvelle consultation ! 👋\n\nQuelle est votre question clinique ? 🩺`,
        timestamp: new Date(),
      },
    ]);
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Stethoscope className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  Dr. {user?.last_name} 👋
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">
                  Assistant clinique — Mode Expert
                </p>
              </div>
            </div>
            <Button
              onClick={resetConversation}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 border-2 border-green-500 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 font-semibold"
            >
              <RotateCcw className="w-4 h-4" />
              Nouvelle consultation
            </Button>
          </div>
        </div>

        <Card className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="h-[calc(100vh-320px)] overflow-y-auto px-6 py-6 bg-gradient-to-b from-green-50/30 to-white dark:from-gray-900 dark:to-gray-950">
            {messages.length === 1 && (
              <div className="mb-8">
                <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                  {SUGGESTIONS.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInput(suggestion.text)}
                      className="group p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 rounded-xl text-left transition-all border-2 border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600 hover:shadow-md"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-gradient-to-br ${suggestion.color} rounded-lg flex items-center justify-center text-2xl`}>
                          {suggestion.icon}
                        </div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                          {suggestion.text}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div key={index} className={`flex gap-3 mb-6 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {message.role === 'assistant' && (
                  <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-white p-1">
                    <img src="/logoR2.png" alt="Remadia" className="w-full h-full object-contain scale-150" />
                  </div>
                )}
                <div className={`max-w-[80%] ${message.role === 'user' ? 'order-1' : ''}`}>
                  <div className={`rounded-2xl px-5 py-4 shadow-sm ${message.role === 'user' ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700'}`}>
                    {message.isTyping ? (
                      <div>
                        <ReactMarkdown components={markdownComponents}>{displayedContent}</ReactMarkdown>
                        <span className="inline-block w-2 h-5 bg-green-500 animate-pulse ml-0.5 align-middle" />
                      </div>
                    ) : (
                      <ReactMarkdown components={markdownComponents}>
                        {typeof message.content === 'string' ? message.content : message.content.text || message.content.content || ''}
                      </ReactMarkdown>
                    )}
                  </div>
                  {!message.isTyping && (
                    <div className={`text-xs text-gray-400 dark:text-gray-500 mt-1.5 px-2 ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                      {message.timestamp?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center flex-shrink-0 shadow-md order-2">
                    <UserIcon className="w-6 h-6 text-white" />
                  </div>
                )}
              </div>
            ))}

            {loading && !isTyping && (
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-white">
                  <img src="/logoR2.png" alt="Remadia" className="w-full h-full object-contain scale-125" />
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-5 py-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      {[0, 150, 300].map((delay) => (
                        <div key={delay} className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                      ))}
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">Analyse en cours...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t-2 border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
            <form onSubmit={handleSend} className="flex gap-3 items-end">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                  placeholder="Question clinique, posologie, interaction médicamenteuse..."
                  rows="1"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-green-400 dark:focus:border-green-500 resize-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
                  style={{ maxHeight: '120px', minHeight: '48px' }}
                  disabled={loading || isTyping}
                />
              </div>
              <Button
                type="submit"
                disabled={!input.trim() || loading || isTyping}
                className="h-12 w-12 bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl disabled:opacity-50 shadow-lg flex-shrink-0"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </form>
            <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full border border-green-200 dark:border-green-800">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                Mode Expert Médecin
              </span>
              <span>•</span>
              <span>Les réponses sont générées par IA — À valider cliniquement</span>
            </div>
          </div>
        </Card>
      </div>

      {showHealthConsent && <HealthDataConsent userId={user.uid} onClose={() => setShowHealthConsent(false)} />}
      {showQuotaModal && <QuotaLimitModal isOpen={showQuotaModal} onClose={() => setShowQuotaModal(false)} onUpgrade={() => navigate('/pricing')} />}
    </DashboardLayout>
  );
};

export default DashboardMedecin;
